<template>
  <div ref="rootEl" class="map-layers">
    <button
      type="button"
      class="locate-btn"
      :class="{ 'locate-btn--on': open }"
      :aria-label="t('map.layersControl.button')"
      :title="t('map.layersControl.button')"
      :aria-expanded="open"
      @click="open = !open"
    >
      <Layers class="locate-btn__icon" />
    </button>

    <div v-if="open" class="map-layers__panel" role="dialog" :aria-label="t('map.layersControl.button')">
      <p class="map-layers__title">{{ t('map.layersControl.base') }}</p>
      <div class="map-layers__segmented" role="radiogroup">
        <button
          type="button"
          role="radio"
          :aria-checked="!is3D"
          class="map-layers__seg"
          :class="{ 'map-layers__seg--on': !is3D }"
          @click="is3D && emit('toggle-3d')"
        >{{ t('map.layersControl.topo') }}</button>
        <button
          type="button"
          role="radio"
          :aria-checked="is3D"
          class="map-layers__seg"
          :class="{ 'map-layers__seg--on': is3D }"
          @click="!is3D && emit('toggle-3d')"
        >{{ t('map.layersControl.topo3d') }}</button>
      </div>

      <p class="map-layers__title">{{ t('map.layersControl.overlays') }}</p>
      <label data-overlay="protectedAreas" class="map-layers__row">
        <input type="checkbox" :checked="overlays.protectedAreas" @change="overlays.toggle('protectedAreas')" />
        <span>{{ t('map.layersControl.protectedAreas') }}</span>
      </label>
      <label data-overlay="routeCoverage" class="map-layers__row">
        <input type="checkbox" :checked="overlays.routeCoverage" @change="overlays.toggle('routeCoverage')" />
        <span>{{ t('map.layersControl.routeCoverage') }}</span>
      </label>

      <p class="map-layers__title">{{ t('map.layersControl.poi') }}</p>
      <div v-for="group in POI_GROUPS" :key="group.id" :data-poi-group="group.id" class="map-layers__row map-layers__row--group">
        <label class="map-layers__label">
          <input
            type="checkbox"
            :checked="poiLayers.enabledGroups.includes(group.id)"
            @change="poiLayers.toggle(group.id)"
          />
          <span>{{ t(`map.layersControl.poiGroup.${group.id}`) }}</span>
        </label>
        <InfoTooltip :text="t(`map.layersControl.poiGroupInfo.${group.id}`)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";
import { useI18n } from "vue-i18n";
import { Layers } from "lucide-vue-next";
import { POI_GROUPS } from "../map/poiVisibility";
import { usePoiLayersStore } from "../store/poiLayers";
import { useMapOverlaysStore } from "../store/mapOverlays";
import InfoTooltip from "./InfoTooltip.vue";

const overlays = useMapOverlaysStore();

defineProps<{ is3D: boolean }>();
const emit = defineEmits<{ (e: "toggle-3d"): void }>();

const { t } = useI18n();
const poiLayers = usePoiLayersStore();
const open = ref(false);
const rootEl = ref<HTMLElement | null>(null);

// Закрытие по клику мимо панели и по Escape - без полноэкранного оверлея,
// чтобы карта под панелью оставалась живой (панель открывается рядом с
// картой, а не поверх неё).
const onDocPointerDown = (e: PointerEvent) => {
  if (open.value && rootEl.value && !rootEl.value.contains(e.target as Node)) open.value = false;
};
const onKeydown = (e: KeyboardEvent) => {
  if (e.key === "Escape") open.value = false;
};
onMounted(() => {
  document.addEventListener("pointerdown", onDocPointerDown);
  document.addEventListener("keydown", onKeydown);
});
onBeforeUnmount(() => {
  document.removeEventListener("pointerdown", onDocPointerDown);
  document.removeEventListener("keydown", onKeydown);
});
</script>

<style scoped>
/* Без position: relative - панель позиционируется от .map-controls целиком
   (она absolute), а не от одной кнопки: тулбар стоит у нижнего края карты,
   и панель, растущая вниз от кнопки, вылезала бы за экран. Низ панели
   совпадает с низом тулбара, растёт вверх. */
.map-layers__panel {
  position: absolute;
  bottom: 0;
  right: calc(100% + 8px);
  width: 232px;
  max-height: min(70vh, 520px);
  overflow-y: auto;
  padding: 12px;
  border-radius: calc(var(--radius) - 2px);
  background: var(--card-hex);
  border: 1px solid hsl(var(--border));
  box-shadow: var(--shadow-sm);
  color: hsl(var(--foreground));
  font-size: 12.5px;
}
.map-layers__title {
  margin: 0 0 6px;
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: hsl(var(--muted-foreground));
}
.map-layers__title:not(:first-child) {
  margin-top: 12px;
}
.map-layers__segmented {
  display: flex;
  padding: 2px;
  border-radius: 8px;
  background: hsl(var(--muted));
}
.map-layers__seg {
  flex: 1;
  padding: 5px 8px;
  border-radius: 6px;
  font-weight: 600;
  color: hsl(var(--muted-foreground));
}
.map-layers__seg--on {
  background: hsl(var(--card));
  color: hsl(var(--foreground));
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
}
.map-layers__row {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  cursor: pointer;
}
.map-layers__row--group {
  justify-content: space-between;
  cursor: default;
}
.map-layers__label {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  cursor: pointer;
}
.map-layers__row input {
  accent-color: hsl(var(--primary));
}
</style>
